import React, { useEffect, useState } from "react";
import styled, { keyframes, css } from "styled-components";
import AdminLayout from "./AdminLayout";
import { getAllOrders, updateOrderStatus } from "../../firebase/orderService";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const slideDown = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ── Page header ── */
const PageHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 40px;
`;
const PageTitle = styled.h1`
  color: #fff;
  font-size: 2rem;
  font-weight: 400;
  letter-spacing: 0.05em;
  margin: 0 0 4px 0;
`;
const PageSub = styled.p`
  color: #444;
  font-size: 0.72rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  margin: 0;
`;

/* ── Status badge ── */
const STATUS_COLORS = {
  pending: { border: "#f39c12", color: "#f39c12" },
  paid: { border: "#2ecc71", color: "#2ecc71" },
  shipped: { border: "#3498db", color: "#3498db" },
  delivered: { border: "#9b59b6", color: "#9b59b6" },
  cancelled: { border: "#e74c3c", color: "#e74c3c" },
};
const StatusBadge = styled.span`
  display: inline-block;
  font-size: 0.58rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 3px 10px;
  border: 1px solid ${(p) => STATUS_COLORS[p.$s]?.border || "#444"};
  color: ${(p) => STATUS_COLORS[p.$s]?.color || "#888"};
  white-space: nowrap;
`;

/* ── Table shell ── */
const Table = styled.div`
  border: 1px solid #1e1e1e;
  animation: ${fadeIn} 0.4s ease forwards;
`;

const COLS = "44px 140px 1.4fr 1fr 90px 140px 120px 44px";

const HeaderRow = styled.div`
  display: grid;
  grid-template-columns: ${COLS};
  background: #0a0a0a;
  border-bottom: 1px solid #1e1e1e;
`;

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: ${COLS};
  border-bottom: ${(p) => (p.$expanded ? "none" : "1px solid #1e1e1e")};
  background: ${(p) => (p.$expanded ? "#141414" : "transparent")};
  transition: background 0.2s;
  cursor: pointer;

  &:hover {
    background: #141414;
  }
  &:last-of-type {
    border-bottom: none;
  }
`;

const Cell = styled.div`
  padding: 14px 14px;
  color: ${(p) => (p.$header ? "#444" : "#aaa")};
  font-size: ${(p) => (p.$header ? "0.6rem" : "0.8rem")};
  letter-spacing: ${(p) => (p.$header ? "0.15em" : "0.02em")};
  text-transform: ${(p) => (p.$header ? "uppercase" : "none")};
  display: flex;
  align-items: center;
  border-right: 1px solid #1e1e1e;
  overflow: hidden;

  &:last-child {
    border-right: none;
    justify-content: center;
  }
  &:first-child {
    justify-content: center;
  }
`;

const ExpandIcon = styled.span`
  color: #444;
  font-size: 0.75rem;
  transition:
    transform 0.25s ease,
    color 0.2s;
  transform: ${(p) => (p.$open ? "rotate(180deg)" : "rotate(0deg)")};
  display: block;

  ${SummaryRow}:hover & {
    color: #888;
  }
`;

const Mono = styled.span`
  font-family: monospace;
  font-size: 0.7rem;
  color: #555;
  letter-spacing: 0.06em;
`;

const ProductThumb = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  background: #1a1a1a;
  overflow: hidden;
  margin-right: 10px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const StatusSelect = styled.select`
  background: #0a0a0a;
  border: 1px solid #2a2a2a;
  color: #aaa;
  padding: 5px 8px;
  font-size: 0.68rem;
  font-family: "Georgia", serif;
  letter-spacing: 0.06em;
  outline: none;
  cursor: pointer;
  width: 100%;

  option {
    background: #111;
  }
  &:focus {
    border-color: #444;
  }
`;

/* ── Expanded detail panel ── */
const DetailPanel = styled.div`
  border-bottom: 1px solid #1e1e1e;
  background: #0d0d0b;
  animation: ${slideDown} 0.25s ease forwards;
  overflow: hidden;
`;

/* top strip — order meta */
const MetaStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  padding: 16px 24px;
  border-bottom: 1px solid #1a1a1a;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const MetaLabel = styled.span`
  font-size: 0.58rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #3a3a3a;
`;

const MetaValue = styled.span`
  font-size: 0.78rem;
  color: #888;
  letter-spacing: 0.03em;
`;

/* main body */
const DetailBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px 240px;
  gap: 0;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 240px;
  }
`;

const DetailSection = styled.div`
  padding: 24px;
  border-right: 1px solid #1a1a1a;

  &:last-child {
    border-right: none;
  }
`;

const DetailSectionTitle = styled.p`
  font-size: 0.6rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: #3a3a3a;
  margin: 0 0 16px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #1a1a1a;
`;

/* items list */
const DownloadBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  font-size: 0.6rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #c9a96e;
  border: 1px solid rgba(201, 169, 110, 0.3);
  padding: 6px 14px;
  text-decoration: none;
  transition:
    border-color 0.2s,
    background 0.2s;
  cursor: pointer;

  &:hover {
    border-color: #c9a96e;
    background: rgba(201, 169, 110, 0.08);
  }
`;

const CustomBadge = styled.span`
  display: inline-block;
  font-size: 0.55rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  padding: 2px 8px;
  border: 1px solid rgba(201, 169, 110, 0.4);
  color: rgba(201, 169, 110, 0.8);
  background: rgba(201, 169, 110, 0.06);
`;

const ItemsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const OrderItem = styled.div`
  display: flex;
  gap: 16px;
  padding: 14px;
  background: #111;
  border: 1px solid #1a1a1a;
`;

const ItemImage = styled.div`
  width: 72px;
  height: 72px;
  flex-shrink: 0;
  background: #1a1a1a;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const NoThumb = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #2a2a2a;
  font-size: 0.55rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

const ItemDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemName = styled.p`
  color: #ccc;
  font-size: 0.88rem;
  margin: 0;
  letter-spacing: 0.02em;
`;

const ItemId = styled.p`
  font-family: monospace;
  font-size: 0.65rem;
  color: #3a3a3a;
  margin: 0;
  letter-spacing: 0.06em;
`;

const ItemTags = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Tag = styled.span`
  font-size: 0.6rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 3px 8px;
  border: 1px solid #222;
  color: #555;
`;

const GoldTag = styled(Tag)`
  border-color: rgba(201, 169, 110, 0.25);
  color: rgba(201, 169, 110, 0.7);
`;

const ItemPrice = styled.p`
  color: #888;
  font-size: 0.82rem;
  margin: 0 0 0 auto;
  align-self: flex-start;
  white-space: nowrap;
`;

/* shipping info */
const InfoGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const InfoLabel = styled.span`
  font-size: 0.58rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #333;
`;

const InfoValue = styled.span`
  font-size: 0.82rem;
  color: #888;
  line-height: 1.45;
`;

/* totals */
const TotalsBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 10px;
  border-bottom: 1px solid #1a1a1a;

  &:last-child {
    border-bottom: none;
    padding-top: 10px;
    border-top: 1px solid #222;
  }
`;

const TotalLabel = styled.span`
  font-size: 0.62rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #444;
`;

const TotalValue = styled.span`
  font-size: ${(p) => (p.$big ? "1.1rem" : "0.82rem")};
  color: ${(p) => (p.$big ? "#f5ede0" : "#888")};
  letter-spacing: 0.04em;
`;

/* empty / loading */
const EmptyState = styled.div`
  color: #333;
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-align: center;
  padding: 80px;
  border: 1px dashed #1e1e1e;
`;

/* ══════════════════════════════════════
   HELPERS
══════════════════════════════════════ */
const formatDate = (ts, includeTime = false) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const date = d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  if (!includeTime) return date;
  const time = d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${date} at ${time}`;
};

const getItems = (order) => {
  if (order.items && order.items.length > 0) return order.items;
  if (order.product)
    return [{ ...order.product, quantity: 1, productId: order.product.id }];
  return [];
};

const getTotal = (order) => {
  if (order.total) return order.total;
  const items = getItems(order);
  return items.reduce((s, i) => s + (i.price || 0) * (i.quantity || 1), 0);
};

/* ══════════════════════════════════════
   COMPONENT
══════════════════════════════════════ */
const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    getAllOrders()
      .then(setOrders)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleStatusChange = async (e, orderId) => {
    e.stopPropagation();
    const newStatus = e.target.value;
    try {
      await updateOrderStatus(orderId, newStatus);
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o)),
      );
    } catch (err) {
      console.error(err);
    }
  };

  const toggle = (id) => setExpanded((prev) => (prev === id ? null : id));

  return (
    <AdminLayout>
      <PageHeader>
        <div>
          <PageTitle>Orders</PageTitle>
          <PageSub>
            {orders.length} total order{orders.length !== 1 ? "s" : ""}
          </PageSub>
        </div>
      </PageHeader>

      {loading ? (
        <EmptyState>Loading...</EmptyState>
      ) : orders.length === 0 ? (
        <EmptyState>No orders yet.</EmptyState>
      ) : (
        <Table>
          {/* ── Header ── */}
          <HeaderRow>
            <Cell $header></Cell>
            <Cell $header>Order ID</Cell>
            <Cell $header>Items</Cell>
            <Cell $header>Customer</Cell>
            <Cell $header>Total</Cell>
            <Cell $header>Date</Cell>
            <Cell $header>Status</Cell>
            <Cell $header></Cell>
          </HeaderRow>

          {orders.map((order) => {
            const items = getItems(order);
            const total = getTotal(order);
            const isOpen = expanded === order.id;
            const firstItem = items[0];

            return (
              <React.Fragment key={order.id}>
                {/* ── Summary row ── */}
                <SummaryRow $expanded={isOpen} onClick={() => toggle(order.id)}>
                  {/* expand icon */}
                  <Cell>
                    <ExpandIcon $open={isOpen}>▾</ExpandIcon>
                  </Cell>

                  {/* order id */}
                  <Cell>
                    <div>
                      <Mono>#{order.id.slice(0, 8)}</Mono>
                      <div
                        style={{
                          color: "#2a2a2a",
                          fontSize: "0.6rem",
                          marginTop: "2px",
                          letterSpacing: "0.08em",
                        }}
                      >
                        {order.id.slice(8, 16)}…
                      </div>
                    </div>
                  </Cell>

                  {/* items preview */}
                  <Cell>
                    <ProductThumb>
                      {firstItem?.imageUrl ? (
                        <img src={firstItem.imageUrl} alt={firstItem.name} />
                      ) : (
                        <div
                          style={{
                            width: "100%",
                            height: "100%",
                            background: "#1a1a1a",
                          }}
                        />
                      )}
                    </ProductThumb>
                    <div>
                      <div
                        style={{
                          color: "#ccc",
                          fontSize: "0.8rem",
                          marginBottom: "2px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "180px",
                        }}
                      >
                        {firstItem?.name || "—"}
                      </div>
                      {items.length > 1 && (
                        <div
                          style={{
                            color: "#444",
                            fontSize: "0.65rem",
                            letterSpacing: "0.1em",
                          }}
                        >
                          +{items.length - 1} more item
                          {items.length > 2 ? "s" : ""}
                        </div>
                      )}
                    </div>
                  </Cell>

                  {/* customer */}
                  <Cell>
                    <div>
                      <div style={{ color: "#ccc", marginBottom: "2px" }}>
                        {order.shipping?.name || "—"}
                      </div>
                      <div style={{ color: "#333", fontSize: "0.68rem" }}>
                        {order.shipping?.city || ""}
                        {order.shipping?.country
                          ? `, ${order.shipping.country}`
                          : ""}
                      </div>
                    </div>
                  </Cell>

                  {/* total */}
                  <Cell>
                    <span
                      style={{
                        color: "#f5ede0",
                        fontFamily: "Cormorant Garamond, serif",
                        fontSize: "1rem",
                      }}
                    >
                      €{total.toFixed(2)}
                    </span>
                  </Cell>

                  {/* date */}
                  <Cell>
                    <div>
                      <div style={{ color: "#888" }}>
                        {formatDate(order.createdAt)}
                      </div>
                    </div>
                  </Cell>

                  {/* status */}
                  <Cell onClick={(e) => e.stopPropagation()}>
                    <StatusSelect
                      value={order.status || "pending"}
                      onChange={(e) => handleStatusChange(e, order.id)}
                    >
                      <option value="pending">Pending</option>
                      <option value="paid">Paid</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </StatusSelect>
                  </Cell>

                  {/* expand arrow (right) */}
                  <Cell>
                    <ExpandIcon $open={isOpen}>▾</ExpandIcon>
                  </Cell>
                </SummaryRow>

                {/* ── Expanded detail panel ── */}
                {isOpen && (
                  <DetailPanel>
                    {/* meta strip */}
                    <MetaStrip>
                      <MetaItem>
                        <MetaLabel>Full Order ID</MetaLabel>
                        <MetaValue
                          style={{
                            fontFamily: "monospace",
                            fontSize: "0.72rem",
                          }}
                        >
                          {order.id}
                        </MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Placed</MetaLabel>
                        <MetaValue>
                          {formatDate(order.createdAt, true)}
                        </MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Status</MetaLabel>
                        <StatusBadge $s={order.status}>
                          {order.status || "pending"}
                        </StatusBadge>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Items</MetaLabel>
                        <MetaValue>
                          {items.length} item{items.length !== 1 ? "s" : ""}
                        </MetaValue>
                      </MetaItem>
                      <MetaItem>
                        <MetaLabel>Order Total</MetaLabel>
                        <MetaValue
                          style={{
                            color: "#f5ede0",
                            fontFamily: "Cormorant Garamond, serif",
                            fontSize: "1rem",
                          }}
                        >
                          €{total.toFixed(2)}
                        </MetaValue>
                      </MetaItem>
                    </MetaStrip>

                    <DetailBody>
                      {/* ── Column 1: Items ── */}
                      <DetailSection>
                        <DetailSectionTitle>Ordered Items</DetailSectionTitle>
                        <ItemsList>
                          {items.map((item, idx) => (
                            <OrderItem key={idx}>
                              <ItemImage>
                                {item.imageUrl ? (
                                  <img src={item.imageUrl} alt={item.name} />
                                ) : (
                                  <NoThumb>No img</NoThumb>
                                )}
                              </ItemImage>
                              <ItemDetails>
                                <ItemName>
                                  {item.name || "—"}
                                  {item.isCustom && (
                                    <CustomBadge style={{ marginLeft: "8px" }}>
                                      Custom Print
                                    </CustomBadge>
                                  )}
                                </ItemName>
                                <ItemId>
                                  ID: {item.productId || item.id || "—"}
                                </ItemId>
                                {item.isCustom && item.imageWidth && (
                                  <ItemId>
                                    {item.imageWidth}×{item.imageHeight}px ·{" "}
                                    {item.imageFormat?.toUpperCase()} ·{" "}
                                    {(item.imageBytes / 1024 / 1024).toFixed(1)}
                                    MB
                                  </ItemId>
                                )}
                                <ItemTags>
                                  {item.frameType && (
                                    <GoldTag>{item.frameType}</GoldTag>
                                  )}
                                  {item.size && <Tag>{item.size}</Tag>}
                                  {item.category && <Tag>{item.category}</Tag>}
                                  {item.quantity > 1 && (
                                    <Tag>×{item.quantity}</Tag>
                                  )}
                                </ItemTags>
                                {item.isCustom && item.originalUrl && (
                                  <DownloadBtn
                                    href={item.originalUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    ↓ Download Full-Res Image
                                  </DownloadBtn>
                                )}
                              </ItemDetails>
                              <ItemPrice>
                                €
                                {(
                                  (item.price || 0) * (item.quantity || 1)
                                ).toFixed(2)}
                                {item.quantity > 1 && (
                                  <div
                                    style={{
                                      color: "#333",
                                      fontSize: "0.65rem",
                                      textAlign: "right",
                                      marginTop: "2px",
                                    }}
                                  >
                                    €{(item.price || 0).toFixed(2)} each
                                  </div>
                                )}
                              </ItemPrice>
                            </OrderItem>
                          ))}
                        </ItemsList>
                      </DetailSection>

                      {/* ── Column 2: Shipping ── */}
                      <DetailSection>
                        <DetailSectionTitle>
                          Customer & Shipping
                        </DetailSectionTitle>
                        <InfoGrid>
                          <InfoRow>
                            <InfoLabel>Full Name</InfoLabel>
                            <InfoValue>{order.shipping?.name || "—"}</InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Email</InfoLabel>
                            <InfoValue
                              style={{ color: "#c9a96e", fontSize: "0.78rem" }}
                            >
                              {order.shipping?.email ? (
                                <a
                                  href={`mailto:${order.shipping.email}`}
                                  style={{
                                    color: "inherit",
                                    textDecoration: "none",
                                  }}
                                >
                                  {order.shipping.email}
                                </a>
                              ) : (
                                "—"
                              )}
                            </InfoValue>
                          </InfoRow>
                          <InfoRow>
                            <InfoLabel>Address</InfoLabel>
                            <InfoValue>
                              {order.shipping?.address || "—"}
                              <br />
                              {order.shipping?.postalCode}{" "}
                              {order.shipping?.city}
                              <br />
                              {order.shipping?.country}
                            </InfoValue>
                          </InfoRow>
                        </InfoGrid>
                      </DetailSection>

                      {/* ── Column 3: Totals ── */}
                      <DetailSection>
                        <DetailSectionTitle>Payment Summary</DetailSectionTitle>
                        <TotalsBox>
                          {items.map((item, idx) => (
                            <TotalRow key={idx}>
                              <TotalLabel
                                style={{
                                  maxWidth: "130px",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {item.name}
                              </TotalLabel>
                              <TotalValue>
                                €
                                {(
                                  (item.price || 0) * (item.quantity || 1)
                                ).toFixed(2)}
                              </TotalValue>
                            </TotalRow>
                          ))}
                          <TotalRow>
                            <TotalLabel>Shipping</TotalLabel>
                            <TotalValue style={{ color: "#2ecc71" }}>
                              Free
                            </TotalValue>
                          </TotalRow>
                          <TotalRow>
                            <TotalLabel>Total</TotalLabel>
                            <TotalValue $big>€{total.toFixed(2)}</TotalValue>
                          </TotalRow>
                        </TotalsBox>
                      </DetailSection>
                    </DetailBody>
                  </DetailPanel>
                )}
              </React.Fragment>
            );
          })}
        </Table>
      )}
    </AdminLayout>
  );
};

export default AdminOrders;
